import { useState, useEffect, Fragment } from 'react';
import { Target, Plus, Eye, Pencil, Trash2, X, Check, Search, Filter, Calendar, Users, Building2, Clock, CheckCircle, XCircle, AlertCircle, Copy, ExternalLink, Star, Sparkles, TrendingUp, Activity, ChevronDown } from 'lucide-react';
import { Dialog, Listbox, Transition } from '@headlessui/react';
import { CastingBrief } from '../lib/supabase';
import { Influencer, normalizeInfluencer } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import InfluencerAvatar from '../components/InfluencerAvatar';

const PLATFORM_ICONS = {
  Instagram: {
    icon: 'https://cdn.simpleicons.org/instagram',
    color: 'text-pink-500',
  },
  YouTube: {
    icon: 'https://cdn.simpleicons.org/youtube',
    color: 'text-red-500',
  },
  TikTok: {
    icon: 'https://cdn.simpleicons.org/tiktok',
    color: 'text-purple-500',
  },
  Twitch: {
    icon: 'https://cdn.simpleicons.org/twitch',
    color: 'text-violet-500',
  },
  Snapchat: {
    icon: 'https://cdn.simpleicons.org/snapchat',
    color: 'text-yellow-400',
  },
};

export default function Casting() {
  const [castings, setCastings] = useState<CastingBrief[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCasting, setSelectedCasting] = useState<CastingBrief | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [selectedInfluencers, setSelectedInfluencers] = useState<string[]>([]);
  const [influencerSearch, setInfluencerSearch] = useState('');
  const [formData, setFormData] = useState<Partial<CastingBrief>>({
    brand: '',
    name: '',
    description: '',
    requirements: [],
    budget: '',
    startDate: new Date(),
    endDate: new Date(),
    status: 'draft',
    selectedInfluencers: [],
    proposedInfluencers: [],
    deadline: new Date(),
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Charger depuis Supabase
      const { data: supabaseCastings } = await supabase
        .from('casting_briefs')
        .select('*')
        .order('created_at', { ascending: false });

      // Charger uniquement les influenceurs de l'organisation (via organization_influencers)
      const { data: orgInfluencersData, error: orgInfluencersError } = await supabase
        .from('organization_influencers')
        .select('influencer_id');

      if (orgInfluencersError) {
        console.error('Error loading org influencers:', orgInfluencersError);
      }

      const influencerIds = orgInfluencersData?.map(oi => oi.influencer_id) || [];

      // Charger les détails des influenceurs de l'organisation uniquement
      const { data: influencersData, error: influencersError } = await supabase
        .from('influencers')
        .select('id, display_name, full_name, username, profile_image_url, followers_count, engagement_rate, category, verified, bio')
        .in('id', influencerIds.length > 0 ? influencerIds : [''])
        .order('followers_count', { ascending: false })
        .limit(500);

      if (influencersError) {
        console.error('Error loading influencers:', influencersError);
      } else {
        console.log('Influencers loaded:', influencersData?.length || 0);
      }

      // Convert timestamps to proper Date objects and normalize snake_case to camelCase
      const processedData = (supabaseCastings || []).map(casting => ({
        ...casting,
        startDate: casting.start_date ? new Date(casting.start_date) : new Date(),
        endDate: casting.end_date ? new Date(casting.end_date) : new Date(),
        deadline: casting.deadline ? new Date(casting.deadline) : undefined,
        createdAt: casting.created_at ? new Date(casting.created_at) : new Date(),
        updatedAt: casting.updated_at ? new Date(casting.updated_at) : new Date(),
        proposedInfluencers: casting.proposed_influencers || [],
        selectedInfluencers: casting.selected_influencers || [],
        activityLog: casting.activity_log || [],
        publicToken: casting.public_token,
        publicLinkActive: casting.public_link_active,
        agencyId: casting.agency_id
      }));
      
      setCastings(processedData);
      setInfluencers((influencersData || []).map(normalizeInfluencer));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const proposedInfluencersData = selectedInfluencers.map(id => {
        const influencer = influencers.find(inf => inf.id === id);
        return {
          influencerId: id,
          fullName: influencer?.fullName || '',
          status: 'pending' as const,
          brandStatus: 'pending' as const,
        };
      });

      const castingDataForSupabase = {
        brand: formData.brand,
        name: formData.name,
        description: formData.description,
        requirements: formData.requirements || [],
        budget: formData.budget,
        start_date: formData.startDate ? new Date(formData.startDate).toISOString() : new Date().toISOString(),
        end_date: formData.endDate ? new Date(formData.endDate).toISOString() : new Date().toISOString(),
        status: formData.status || 'draft',
        proposed_influencers: proposedInfluencersData,
        selected_influencers: formData.selectedInfluencers || [],
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
        updated_at: new Date().toISOString()
      };

      if (selectedCasting?.id) {
        const { error } = await supabase
          .from('casting_briefs')
          .update(castingDataForSupabase)
          .eq('id', selectedCasting.id);

        if (error) {
          console.error('Error updating casting:', error);
          throw error;
        }
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('agency_id')
          .eq('id', user.id)
          .single();

        if (userError) throw userError;
        if (!userData?.agency_id) throw new Error('User has no agency');

        const { error } = await supabase
          .from('casting_briefs')
          .insert({
            ...castingDataForSupabase,
            agency_id: userData.agency_id,
            created_at: new Date().toISOString()
          });

        if (error) {
          console.error('Error creating casting:', error);
          throw error;
        }
      }

      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving casting:', error);
      alert('Erreur lors de la sauvegarde du casting: ' + (error as Error).message);
    }
  };

  const handleDelete = async () => {
    try {
      if (!selectedCasting?.id) return;

      const { error } = await supabase
        .from('casting_briefs')
        .delete()
        .eq('id', selectedCasting.id);

      if (error) throw error;

      await loadData();
      setIsDeleteModalOpen(false);
      setSelectedCasting(null);
    } catch (error) {
      console.error('Error deleting casting:', error);
    }
  };

  const handleEdit = (casting: CastingBrief) => {
    setSelectedCasting(casting);
    setFormData({
      brand: casting.brand,
      name: casting.name,
      description: casting.description,
      requirements: [...casting.requirements],
      budget: casting.budget,
      startDate: casting.startDate,
      endDate: casting.endDate,
      status: casting.status,
      deadline: casting.deadline,
    });
    setSelectedInfluencers(casting.proposedInfluencers?.map(inf => inf.influencerId) || []);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCasting(null);
    setSelectedInfluencers([]);
    setInfluencerSearch('');
    setFormData({
      brand: '',
      name: '',
      description: '',
      requirements: [],
      budget: '',
      startDate: new Date(),
      endDate: new Date(),
      status: 'draft',
      selectedInfluencers: [],
      proposedInfluencers: [],
      deadline: new Date(),
    });
  };

  const generatePublicLink = async (casting: CastingBrief) => {
    try {
      const publicToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

      const { error } = await supabase
        .from('casting_briefs')
        .update({
          public_token: publicToken,
          public_link_active: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', casting.id!);

      if (error) throw error;

      const publicUrl = `${window.location.origin}/casting/${publicToken}`;
      await navigator.clipboard.writeText(publicUrl);
      setCopiedLink(casting.id!);
      setTimeout(() => setCopiedLink(null), 3000);
      
      await loadData();
    } catch (error) {
      console.error('Error generating public link:', error);
    }
  };

  const formatDateForInput = (date: Date | { seconds: number } | undefined) => {
    if (!date) return '';
    
    try {
      let dateObj: Date;
      
      if (typeof date === 'object' && 'seconds' in date) {
        dateObj = new Date(date.seconds * 1000);
      } else {
        dateObj = new Date(date);
      }
      
      // Check if the date is valid
      if (isNaN(dateObj.getTime())) {
        return '';
      }
      
      return dateObj.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error formatting date for input:', error);
      return '';
    }
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    try {
      let dateObj: Date;
      
      if (typeof date === 'object' && 'seconds' in date) {
        dateObj = new Date(date.seconds * 1000);
      } else {
        dateObj = new Date(date);
      }
      
      // Check if the date is valid
      if (isNaN(dateObj.getTime())) {
        return 'Date invalide';
      }
      
      return dateObj.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date invalide';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return { label: 'Actif', className: 'bg-green-500/20 text-green-300 border-green-500/30' };
      case 'closed':
        return { label: 'Fermé', className: 'bg-red-500/20 text-red-300 border-red-500/30' };
      default:
        return { label: 'Brouillon', className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' };
    }
  };

  const getInfluencerDecisionStats = (casting: CastingBrief) => {
    if (!casting.proposedInfluencers) return { accepted: 0, maybe: 0, rejected: 0, pending: 0 };
    
    return casting.proposedInfluencers.reduce((stats, inf) => {
      switch (inf.brandStatus) {
        case 'accepted':
          stats.accepted++;
          break;
        case 'maybe':
          stats.maybe++;
          break;
        case 'rejected':
          stats.rejected++;
          break;
        default:
          stats.pending++;
      }
      return stats;
    }, { accepted: 0, maybe: 0, rejected: 0, pending: 0 });
  };

  const filteredCastings = castings.filter(casting => {
    const matchesSearch = 
      casting.name.toLowerCase().includes(search.toLowerCase()) ||
      casting.brand.toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || casting.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const activeCastings = castings.filter(c => c.status === 'active').length;
  const totalInfluencers = castings.reduce((sum, c) => sum + (c.proposedInfluencers?.length || 0), 0);
  const totalDecisions = castings.reduce((sum, c) => {
    const stats = getInfluencerDecisionStats(c);
    return sum + stats.accepted + stats.maybe + stats.rejected;
  }, 0);

  return (
    <div className="space-y-8">
      {/* Modern Header with Stats */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl p-8 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24 blur-2xl"></div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Target className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold mb-2">Casting</h1>
                <p className="text-white/80 text-lg">Gérez vos castings et validations marques</p>
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-3 px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 transition-all transform hover:scale-105 border border-white/20"
            >
              <Plus className="w-5 h-5" />
              Nouveau casting
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <span className="text-3xl font-bold">{activeCastings}</span>
              </div>
              <h3 className="text-lg font-semibold mb-1">Castings actifs</h3>
              <p className="text-white/70 text-sm">En cours de validation</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <span className="text-3xl font-bold">{totalInfluencers}</span>
              </div>
              <h3 className="text-lg font-semibold mb-1">Influenceurs proposés</h3>
              <p className="text-white/70 text-sm">Total des propositions</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <span className="text-3xl font-bold">{totalDecisions}</span>
              </div>
              <h3 className="text-lg font-semibold mb-1">Décisions reçues</h3>
              <p className="text-white/70 text-sm">Retours des marques</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-6 relative">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="text"
                placeholder="Rechercher un casting..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Listbox value={statusFilter} onChange={setStatusFilter}>
              {({ open }) => (
                <div className="relative min-w-[150px]">
                  <Listbox.Button className="relative w-full px-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl text-left text-white focus:outline-none focus:ring-1 focus:ring-white/20 cursor-pointer">
                    <span className="block truncate pr-8">
                      {statusFilter === 'all' ? 'Tous les statuts' : statusFilter === 'draft' ? 'Brouillons' : statusFilter === 'active' ? 'Actifs' : 'Fermés'}
                    </span>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-5 w-5 text-white/40" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-[9999] mt-1 w-full bg-[#141414] border border-white/[0.08] rounded-xl shadow-lg max-h-60 overflow-auto focus:outline-none">
                      <Listbox.Option value="all" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                        {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>Tous les statuts</span>}
                      </Listbox.Option>
                      <Listbox.Option value="draft" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                        {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>Brouillons</span>}
                      </Listbox.Option>
                      <Listbox.Option value="active" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                        {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>Actifs</span>}
                      </Listbox.Option>
                      <Listbox.Option value="closed" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                        {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>Fermés</span>}
                      </Listbox.Option>
                    </Listbox.Options>
                  </Transition>
                </div>
              )}
            </Listbox>
          </div>
        </div>
      </div>

      {/* Castings Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/10 border-t-white/60"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {filteredCastings.map((casting) => {
            const stats = getInfluencerDecisionStats(casting);
            const statusBadge = getStatusBadge(casting.status);
            
            return (
              <div
                key={casting.id}
                className="group bg-white/[0.04] backdrop-blur-sm rounded-2xl border border-white/[0.08] overflow-hidden hover:bg-white/[0.06] transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Card Header */}
                <div className="relative p-6 border-b border-white/[0.06]">
                  <div className="absolute top-4 right-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => {
                        setSelectedCasting(casting);
                        setIsDetailModalOpen(true);
                      }}
                      className="p-2 bg-white/[0.08] text-white/60 hover:text-white rounded-lg hover:bg-white/[0.14] transition-all"
                      title="Voir les détails"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(casting)}
                      className="p-2 bg-white/[0.08] text-white/60 hover:text-white rounded-lg hover:bg-white/[0.14] transition-all"
                      title="Modifier"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCasting(casting);
                        setIsDeleteModalOpen(true);
                      }}
                      className="p-2 bg-white/[0.08] text-white/60 hover:text-red-400 rounded-lg hover:bg-white/[0.14] transition-all"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-white mb-2 truncate">
                        {casting.name}
                      </h3>
                      <div className="flex items-center gap-2 mb-3">
                        <Building2 className="w-4 h-4 text-white/40" />
                        <span className="text-white/60 font-medium">{casting.brand}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusBadge.className}`}>
                      {statusBadge.label}
                    </span>
                    <div className="text-right">
                      <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Budget</p>
                      <p className="text-lg font-bold text-white">{casting.budget}</p>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6 space-y-4">
                  <p className="text-white/50 line-clamp-3 leading-relaxed">
                    {casting.description}
                  </p>

                  {/* Timeline */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/[0.04] rounded-xl p-3 border border-white/[0.06]">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-white/40" />
                        <span className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Début</span>
                      </div>
                      <p className="text-sm font-semibold text-white">{formatDate(casting.startDate)}</p>
                    </div>
                    <div className="bg-white/[0.04] rounded-xl p-3 border border-white/[0.06]">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-white/40" />
                        <span className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Fin</span>
                      </div>
                      <p className="text-sm font-semibold text-white">{formatDate(casting.endDate)}</p>
                    </div>
                  </div>

                  {/* Influencers Stats */}
                  <div className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-white/60" />
                        <span className="font-semibold text-white">Influenceurs</span>
                      </div>
                      <span className="text-lg font-bold text-white">
                        {casting.proposedInfluencers?.length || 0}
                      </span>
                    </div>

                    {casting.proposedInfluencers && casting.proposedInfluencers.length > 0 && (
                      <div className="grid grid-cols-4 gap-2">
                        <div className="text-center">
                          <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center mx-auto mb-1">
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          </div>
                          <p className="text-xs text-white/40">Acceptés</p>
                          <p className="text-sm font-bold text-green-400">{stats.accepted}</p>
                        </div>
                        <div className="text-center">
                          <div className="w-8 h-8 rounded-lg bg-yellow-500/20 flex items-center justify-center mx-auto mb-1">
                            <AlertCircle className="w-4 h-4 text-yellow-400" />
                          </div>
                          <p className="text-xs text-white/40">Peut-être</p>
                          <p className="text-sm font-bold text-yellow-400">{stats.maybe}</p>
                        </div>
                        <div className="text-center">
                          <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center mx-auto mb-1">
                            <XCircle className="w-4 h-4 text-red-400" />
                          </div>
                          <p className="text-xs text-white/40">Refusés</p>
                          <p className="text-sm font-bold text-red-400">{stats.rejected}</p>
                        </div>
                        <div className="text-center">
                          <div className="w-8 h-8 rounded-lg bg-white/[0.08] flex items-center justify-center mx-auto mb-1">
                            <Clock className="w-4 h-4 text-white/40" />
                          </div>
                          <p className="text-xs text-white/40">En attente</p>
                          <p className="text-sm font-bold text-white/60">{stats.pending}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={() => generatePublicLink(casting)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white text-black rounded-xl hover:bg-white/90 transition-all font-semibold"
                    >
                      {copiedLink === casting.id ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copié !
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Lien marque
                        </>
                      )}
                    </button>

                    {casting.publicToken && casting.publicLinkActive && (
                      <a
                        href={`/casting/${casting.publicToken}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-white/[0.08] text-white/60 hover:text-white rounded-xl hover:bg-white/[0.14] border border-white/[0.08] transition-all"
                        title="Voir la vue marque"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {filteredCastings.length === 0 && (
            <div className="col-span-full">
              <div className="text-center py-16 bg-white/[0.04] rounded-3xl border border-white/[0.08]">
                <div className="w-20 h-20 rounded-full bg-white/[0.06] flex items-center justify-center mx-auto mb-6">
                  <Target className="w-10 h-10 text-white/30" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  {search || statusFilter !== 'all' ? 'Aucun résultat' : 'Aucun casting'}
                </h3>
                <p className="text-white/50 mb-8 max-w-md mx-auto">
                  {search || statusFilter !== 'all'
                    ? 'Aucun casting ne correspond à vos critères de recherche.'
                    : 'Commencez par créer votre premier casting pour proposer des influenceurs à vos clients.'}
                </p>
                {(!search && statusFilter === 'all') && (
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center gap-3 px-6 py-3 bg-white text-black rounded-xl hover:bg-white/90 transition-all font-semibold"
                  >
                    <Plus className="w-5 h-5" />
                    Créer un casting
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] rounded-2xl max-w-4xl w-full mx-4 border border-white/[0.08] overflow-hidden">
            <div className="absolute top-6 right-6 z-10">
              <button
                onClick={handleCloseModal}
                className="p-2 bg-white/[0.08] hover:bg-white/[0.14] text-white/60 hover:text-white rounded-xl transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-white/[0.08] flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">
                  {selectedCasting ? 'Modifier le casting' : 'Nouveau casting'}
                </h2>
                <p className="text-white/50">
                  {selectedCasting ? 'Mettez à jour les informations du casting' : 'Créez un nouveau casting pour vos clients'}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08]">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-white/60" />
                      Informations générales
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                          Nom du casting
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
                          placeholder="Ex: Campagne été 2024"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                          Marque
                        </label>
                        <input
                          type="text"
                          value={formData.brand}
                          onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                          className="w-full px-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
                          placeholder="Nom de la marque"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                          Budget
                        </label>
                        <input
                          type="text"
                          value={formData.budget}
                          onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                          className="w-full px-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
                          placeholder="Ex: 10 000€"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                          Statut
                        </label>
                        <Listbox value={formData.status} onChange={(value) => setFormData({ ...formData, status: value as CastingBrief['status'] })}>
                          <div className="relative">
                            <Listbox.Button className="relative w-full px-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl text-left text-white focus:outline-none focus:ring-1 focus:ring-white/20 cursor-pointer">
                              <span className="block truncate">
                                {formData.status === 'draft' ? 'Brouillon' : formData.status === 'active' ? 'Actif' : 'Fermé'}
                              </span>
                              <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <ChevronDown className="h-5 w-5 text-white/40" />
                              </span>
                            </Listbox.Button>
                            <Listbox.Options className="absolute z-50 mt-1 w-full bg-[#141414] border border-white/[0.08] rounded-xl shadow-lg max-h-60 overflow-auto focus:outline-none">
                              <Listbox.Option value="draft" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                                {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>Brouillon</span>}
                              </Listbox.Option>
                              <Listbox.Option value="active" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                                {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>Actif</span>}
                              </Listbox.Option>
                              <Listbox.Option value="closed" className={({ active }) => `cursor-pointer select-none relative py-2 px-3 ${active ? 'bg-white/[0.08] text-white' : 'text-white/60'}`}>
                                {({ selected }) => <span className={selected ? 'font-medium' : 'font-normal'}>Fermé</span>}
                              </Listbox.Option>
                            </Listbox.Options>
                          </div>
                        </Listbox>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08]">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-white/60" />
                      Planning
                    </h3>

                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                          Date de début
                        </label>
                        <input
                          type="date"
                          value={formData.startDate ? formatDateForInput(formData.startDate) : ''}
                          onChange={(e) => setFormData({ ...formData, startDate: new Date(e.target.value) })}
                          className="w-full px-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                          Date de fin
                        </label>
                        <input
                          type="date"
                          value={formData.endDate ? formatDateForInput(formData.endDate) : ''}
                          onChange={(e) => setFormData({ ...formData, endDate: new Date(e.target.value) })}
                          className="w-full px-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                          Deadline validation marque
                        </label>
                        <input
                          type="date"
                          value={formData.deadline ? formatDateForInput(formData.deadline) : ''}
                          onChange={(e) => setFormData({ ...formData, deadline: new Date(e.target.value) })}
                          className="w-full px-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08]">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-white/60" />
                      Description & Critères
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                          Description du casting
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={4}
                          className="w-full px-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all resize-none"
                          placeholder="Décrivez le brief de la campagne..."
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/60 mb-2">
                          Critères recherchés
                        </label>
                        <div className="space-y-2">
                          {formData.requirements?.map((req, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={req}
                                onChange={(e) => {
                                  const newRequirements = [...(formData.requirements || [])];
                                  newRequirements[index] = e.target.value;
                                  setFormData({ ...formData, requirements: newRequirements });
                                }}
                                className="flex-1 px-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-lg text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
                                placeholder="Ex: Minimum 10K followers"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newRequirements = formData.requirements?.filter((_, i) => i !== index) || [];
                                  setFormData({ ...formData, requirements: newRequirements });
                                }}
                                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const newRequirements = [...(formData.requirements || []), ''];
                              setFormData({ ...formData, requirements: newRequirements });
                            }}
                            className="w-full px-4 py-2 bg-white/[0.06] border border-dashed border-white/[0.08] text-white/60 rounded-xl hover:bg-white/[0.08] hover:text-white transition-all"
                          >
                            + Ajouter un critère
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08]">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-white/60" />
                      Influenceurs sélectionnés ({selectedInfluencers.length})
                    </h3>

                    <div className="space-y-4">
                      <div className="bg-white/[0.06] border border-white/[0.08] rounded-xl p-4 mb-4">
                        <p className="text-sm text-white/60 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>
                            Seuls les influenceurs ajoutés à votre organisation sont disponibles ici.
                            Pour ajouter de nouveaux influenceurs, rendez-vous sur la page{' '}
                            <a href="/app/discover" className="font-semibold underline hover:text-white">
                              Discover
                            </a>
                            .
                          </span>
                        </p>
                      </div>

                      <div className="relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/30" />
                        <input
                          type="text"
                          placeholder="Rechercher un influenceur..."
                          value={influencerSearch}
                          onChange={(e) => setInfluencerSearch(e.target.value)}
                          className="w-full pl-12 pr-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
                        />
                      </div>

                      <div className="max-h-64 overflow-y-auto space-y-2 bg-white/[0.04] rounded-xl border border-white/[0.08] p-3">
                        {(() => {
                          const filtered = influencers.filter(inf => {
                            if (!influencerSearch.trim()) return true;
                            const searchLower = influencerSearch.toLowerCase().trim();
                            const username = (inf.username || '').toLowerCase();
                            const displayName = (inf.display_name || inf.name || inf.fullName || '').toLowerCase();

                            return username.includes(searchLower) ||
                                   `@${username}`.includes(searchLower) ||
                                   displayName.includes(searchLower);
                          });

                          if (filtered.length === 0) {
                            return (
                              <div className="text-center py-8">
                                <p className="text-white/50 mb-2">Aucun influenceur trouvé</p>
                                {influencerSearch.trim() && (
                                  <p className="text-sm text-white/30">
                                    Essayez une autre recherche ou ajoutez l'influenceur depuis{' '}
                                    <a href="/app/discover" className="text-white/60 hover:text-white underline">
                                      Discover
                                    </a>
                                  </p>
                                )}
                              </div>
                            );
                          }

                          return filtered.map((influencer) => (
                            <div
                              key={influencer.id}
                              className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${
                                selectedInfluencers.includes(influencer.id!)
                                  ? 'bg-white/[0.08] border-white/20 text-white'
                                  : 'bg-white/[0.04] border-white/[0.06] hover:bg-white/[0.08]'
                              }`}
                              onClick={() => {
                                if (selectedInfluencers.includes(influencer.id!)) {
                                  setSelectedInfluencers(prev => prev.filter(id => id !== influencer.id));
                                } else {
                                  setSelectedInfluencers(prev => [...prev, influencer.id!]);
                                }
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <InfluencerAvatar
                                  profileImageUrl={influencer.profile_image_url}
                                  name={influencer.fullName || influencer.username}
                                  size="md"
                                />
                                <div>
                                  <p className="font-medium text-white">{influencer.fullName || influencer.name || influencer.display_name}</p>
                                  <p className="text-sm text-white/50">@{influencer.username || 'N/A'}</p>
                                </div>
                              </div>
                              {selectedInfluencers.includes(influencer.id!) && (
                                <CheckCircle className="w-5 h-5 text-white" />
                              )}
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t border-white/[0.08]">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-3 bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08] rounded-xl transition-all font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-white text-black rounded-xl hover:bg-white/90 transition-all font-semibold"
                >
                  {selectedCasting ? 'Mettre à jour' : 'Créer le casting'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Detail Modal */}
      <Dialog
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] rounded-2xl max-w-4xl w-full mx-4 border border-white/[0.08] overflow-hidden">
            {selectedCasting && (
              <>
                <div className="absolute top-6 right-6 z-10">
                  <button
                    onClick={() => setIsDetailModalOpen(false)}
                    className="p-2 bg-white/[0.08] hover:bg-white/[0.14] text-white/60 hover:text-white rounded-xl transition-all"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-8 space-y-8">
                  {/* Header */}
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-white/[0.08] flex items-center justify-center mx-auto mb-4">
                      <Target className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-2">{selectedCasting.name}</h2>
                    <div className="flex items-center justify-center gap-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-white/40" />
                        <span className="text-white/60 font-medium">{selectedCasting.brand}</span>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadge(selectedCasting.status).className}`}>
                        {getStatusBadge(selectedCasting.status).label}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div className="space-y-6">
                      <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08]">
                        <h3 className="text-lg font-semibold text-white mb-4">Description</h3>
                        <p className="text-white/60 leading-relaxed">{selectedCasting.description}</p>
                      </div>

                      <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08]">
                        <h3 className="text-lg font-semibold text-white mb-4">Informations</h3>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <span className="text-white/50">Budget</span>
                            <span className="font-semibold text-white">{selectedCasting.budget}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-white/50">Période</span>
                            <span className="font-semibold text-white">
                              {formatDate(selectedCasting.startDate)} - {formatDate(selectedCasting.endDate)}
                            </span>
                          </div>
                          {selectedCasting.deadline && (
                            <div className="flex items-center justify-between">
                              <span className="text-white/50">Deadline validation</span>
                              <span className="font-semibold text-white">{formatDate(selectedCasting.deadline)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {selectedCasting.requirements && selectedCasting.requirements.length > 0 && (
                        <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08]">
                          <h3 className="text-lg font-semibold text-white mb-4">Critères recherchés</h3>
                          <ul className="space-y-2">
                            {selectedCasting.requirements.map((req, index) => (
                              <li key={index} className="flex items-center gap-3">
                                <CheckCircle className="w-5 h-5 text-white/40 flex-shrink-0" />
                                <span className="text-white/70">{req}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08]">
                        <h3 className="text-lg font-semibold text-white mb-4">
                          Influenceurs proposés ({selectedCasting.proposedInfluencers?.length || 0})
                        </h3>

                        {selectedCasting.proposedInfluencers && selectedCasting.proposedInfluencers.length > 0 ? (
                          <div className="space-y-3">
                            {selectedCasting.proposedInfluencers.map((proposedInf, index) => {
                              const influencer = influencers.find(inf => inf.id === proposedInf.influencerId);
                              const statusBadge = proposedInf.brandStatus === 'accepted'
                                ? { icon: CheckCircle, className: 'bg-green-500/20 text-green-400 border-green-500/30' }
                                : proposedInf.brandStatus === 'maybe'
                                ? { icon: AlertCircle, className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' }
                                : proposedInf.brandStatus === 'rejected'
                                ? { icon: XCircle, className: 'bg-red-500/20 text-red-400 border-red-500/30' }
                                : { icon: Clock, className: 'bg-white/[0.08] text-white/60 border-white/[0.08]' };

                              return (
                                <div key={index} className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.08]">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                      <InfluencerAvatar
                                        profileImageUrl={proposedInf.profileImageUrl || influencer?.profile_image_url}
                                        name={proposedInf.fullName || proposedInf.displayName || influencer?.username || proposedInf.username}
                                        size="md"
                                      />
                                      <div>
                                        <p className="font-medium text-white">
                                          {proposedInf.fullName || proposedInf.displayName || influencer?.display_name || influencer?.fullName}
                                        </p>
                                        <p className="text-sm text-white/50">
                                          @{proposedInf.username || influencer?.username}
                                        </p>
                                      </div>
                                    </div>
                                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${statusBadge.className}`}>
                                      <statusBadge.icon className="w-4 h-4" />
                                      <span className="text-sm font-medium">
                                        {proposedInf.brandStatus === 'accepted' ? 'Accepté' :
                                         proposedInf.brandStatus === 'maybe' ? 'Peut-être' :
                                         proposedInf.brandStatus === 'rejected' ? 'Refusé' : 'En attente'}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Commentaire de la marque */}
                                  {proposedInf.brandComment && (
                                    <div className="mt-4 pt-4 border-t border-white/[0.06]">
                                      <h4 className="text-sm font-medium text-white/60 mb-2">Commentaire de la marque</h4>
                                      <div className="bg-white/[0.06] rounded-lg p-3 border border-white/[0.08]">
                                        <p className="text-white/70 text-sm">{proposedInf.brandComment}</p>
                                        {proposedInf.brandDecisionDate && (
                                          <p className="text-xs text-white/40 mt-2">
                                            Soumis le {formatDate(proposedInf.brandDecisionDate)}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {influencer && influencer.platforms && (
                                    <div className="flex flex-wrap gap-2">
                                      {influencer.platforms.slice(0, 3).map((platform, platformIndex) => (
                                        <div key={platformIndex} className="flex items-center gap-2 px-2 py-1 bg-white/[0.06] rounded-lg">
                                          <img
                                            src={PLATFORM_ICONS[platform.name as keyof typeof PLATFORM_ICONS]?.icon}
                                            alt={platform.name}
                                            className="w-4 h-4"
                                          />
                                          <span className="text-xs text-white/50">{platform.audience}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Users className="w-12 h-12 text-white/30 mx-auto mb-3" />
                            <p className="text-white/50">Aucun influenceur proposé</p>
                          </div>
                        )}
                      </div>

                      {selectedCasting.publicToken && selectedCasting.publicLinkActive && (
                        <div className="bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08]">
                          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            <ExternalLink className="w-5 h-5 text-white/60" />
                            Lien public actif
                          </h3>
                          <div className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.08]">
                            <p className="text-sm text-white/50 mb-2">Lien de validation pour la marque :</p>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 text-sm bg-white/[0.06] px-3 py-2 rounded-lg text-white/70 truncate">
                                {`${window.location.origin}/casting/${selectedCasting.publicToken}`}
                              </code>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(`${window.location.origin}/casting/${selectedCasting.publicToken}`);
                                  setCopiedLink(selectedCasting.id!);
                                  setTimeout(() => setCopiedLink(null), 2000);
                                }}
                                className="p-2 bg-white/[0.08] text-white/60 hover:bg-white/[0.14] rounded-lg transition-all"
                              >
                                {copiedLink === selectedCasting.id ? (
                                  <Check className="w-4 h-4" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Dialog>

      {/* Delete Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] rounded-2xl max-w-md w-full mx-4 p-6 border border-white/[0.08]">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Supprimer le casting</h3>
              <p className="text-white/50 mb-6">
                Êtes-vous sûr de vouloir supprimer le casting "{selectedCasting?.name}" ? Cette action est irréversible.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-6 py-3 bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08] rounded-xl transition-all font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="px-6 py-3 bg-red-500/20 text-red-400 border border-red-500/20 rounded-xl hover:bg-red-500/30 transition-all font-semibold"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}